import React, { Component } from 'react';
import PropTypes from 'prop-types';
import PropTypesImmutable from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { Spin, Table, Button, Upload, Modal, notification } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { List } from 'immutable';
import * as actions from '../../actions/actionCreators/registerEntityDocumnetActionCreators';
import * as selectors from '../../reducers/registerEntityDocumentsReducer';
import { updateLegalEntityActionCreator } from '../../actions/actionCreators/registerActionCreators';
import { getLoadingSelector } from '../../utils/asyncHelpers';
import { getLegalEntityDocuments } from '../../schemas/legalEntityDocument';
import { formRegisterGeneralFields } from '../../constants/formRegister';
import legalEntityStatus from '../../constants/classificators/legalEntityStatus';
import { FILE_URL } from '../../constants/file';
import { getCsrfCookie } from '../../utils/cookieUtil';
import { RegisterActions } from '../../components';
import { goToLogin, goToSearch } from '../../utils/gotoLink';
import dayjs from '../../utils/dayjs';
import './RegisterDocuments.scss';

const STATUS_ACTIVE = 'ACTIVE';
const DATE_FORMAT = 'DD.MM.YYYY HH:mm:ss';

const sortDocuments = (documents) => {
  return [...documents].sort((a, b) => {
    // First sort by status (ACTIVE first)
    if (a.status === STATUS_ACTIVE && b.status !== STATUS_ACTIVE) return -1;
    if (a.status !== STATUS_ACTIVE && b.status === STATUS_ACTIVE) return 1;
    // Then sort by createdDate descending
    const dateA = a.createdDate
      ? dayjs(a.createdDate, DATE_FORMAT, true)
      : dayjs(0);
    const dateB = b.createdDate
      ? dayjs(b.createdDate, DATE_FORMAT, true)
      : dayjs(0);
    return dateB.valueOf() - dateA.valueOf();
  });
};

export class RegisterDocuments extends Component {
  static propTypes = {
    isLoading: PropTypes.bool.isRequired,
    isStatusChangeLoading: PropTypes.bool.isRequired,
    documents: PropTypesImmutable.list,
    fetchLegalEntityDocuments: PropTypes.func.isRequired,
    addLegalEntityDocument: PropTypes.func.isRequired,
    activateLegalEntityDocument: PropTypes.func.isRequired,
    deactivateLegalEntityDocument: PropTypes.func.isRequired,
    updateLegalEntity: PropTypes.func.isRequired,
    hasPrevious: PropTypes.bool,
    hasDone: PropTypes.bool,
    goPrevious: PropTypes.func,
    legalEntity: PropTypes.object.isRequired,
    isEditMode: PropTypes.bool,
    isAdmin: PropTypes.bool,
    t: PropTypes.func.isRequired,
  };

  static defaultProps = {
    documents: List(),
  };

  componentDidMount = () => {
    const { fetchLegalEntityDocuments, legalEntity } = this.props;
    fetchLegalEntityDocuments(legalEntity.get(formRegisterGeneralFields.ID));
  };

  onMasterSubmit = async () => {
    const { updateLegalEntity, legalEntity, t, isAdmin } = this.props;

    await updateLegalEntity(
      legalEntity
        .set(
          formRegisterGeneralFields.STATUS,
          isAdmin ? legalEntityStatus.ACTIVE : legalEntityStatus.NEW
        )
        .toJS()
    );

    if (!isAdmin) {
      goToLogin();
      notification.info({
        description: t('register.message'),
      });
    } else {
      goToSearch();
    }
  };

  onFileChange = (info) => {
    const {
      addLegalEntityDocument,
      legalEntity,
      fetchLegalEntityDocuments,
      t,
    } = this.props;

    if (info.file.status === 'done') {
      addLegalEntityDocument({
        file: info.file.response,
        legalEntityId: legalEntity.get(formRegisterGeneralFields.ID),
        name: info.file.name,
      }).then(() => {
        fetchLegalEntityDocuments(
          legalEntity.get(formRegisterGeneralFields.ID)
        );
      });
    } else if (info.file.status === 'error') {
      notification.error({
        message: t('documents.uploadError'),
      });
    }
  };

  handleActivate = (id) => {
    const {
      activateLegalEntityDocument,
      fetchLegalEntityDocuments,
      legalEntity,
    } = this.props;
    activateLegalEntityDocument(id).then(() => {
      fetchLegalEntityDocuments(legalEntity.get(formRegisterGeneralFields.ID));
    });
  };

  handleDeactivate = (id) => {
    const {
      deactivateLegalEntityDocument,
      fetchLegalEntityDocuments,
      legalEntity,
    } = this.props;
    deactivateLegalEntityDocument(id).then(() => {
      fetchLegalEntityDocuments(legalEntity.get(formRegisterGeneralFields.ID));
    });
  };

  getColumns = () => {
    const { t, isAdmin } = this.props;

    const baseColumns = [
      {
        title: t('documents.fileName'),
        dataIndex: 'name',
        key: 'name',
        render: (text, record) => (
          <a
            href={`${FILE_URL}/${record.file}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {text}
          </a>
        ),
      },
      {
        title: t('documents.uploadTime'),
        dataIndex: 'createdDate',
        key: 'createdDate',
      },
    ];

    if (isAdmin) {
      baseColumns.push({
        title: t('documents.uploadedBy'),
        dataIndex: 'createdBy',
        key: 'createdBy',
      });
    }

    baseColumns.push({
      title: t('documents.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) =>
        status === STATUS_ACTIVE
          ? t('documents.statusValid')
          : t('documents.statusExpired'),
    });

    if (isAdmin) {
      baseColumns.push({
        title: t('documents.actions'),
        key: 'actions',
        render: (_, record) => (
          <span>
            {record.status === STATUS_ACTIVE ? (
              <Button
                type="link"
                onClick={() => this.handleDeactivate(record.id)}
              >
                {t('documents.deactivate')}
              </Button>
            ) : (
              <Button
                type="link"
                onClick={() => this.handleActivate(record.id)}
              >
                {t('documents.activate')}
              </Button>
            )}
          </span>
        ),
      });
    }

    return baseColumns;
  };

  render() {
    const {
      t,
      isLoading,
      isStatusChangeLoading,
      documents,
      hasPrevious,
      hasDone,
      goPrevious,
      isEditMode,
      isAdmin,
    } = this.props;

    const propsUpload = {
      action: FILE_URL,
      headers: {
        'X-XSRF-TOKEN': getCsrfCookie(),
      },
      onChange: this.onFileChange,
      showUploadList: false,
    };

    const sortedDocuments = sortDocuments(documents.toJS());

    return (
      <div className="register-documents">
        <Spin spinning={isLoading || isStatusChangeLoading}>
          <h2>{t('documents.title')}</h2>

          {isAdmin && (
            <div className="register-documents__upload">
              <h3>{t('documents.addFiles')}</h3>
              <Upload {...propsUpload}>
                <Button icon={<UploadOutlined />}>
                  {t('documents.browse')}
                </Button>
              </Upload>
            </div>
          )}

          <Table
            columns={this.getColumns()}
            dataSource={sortedDocuments}
            rowKey="id"
            pagination={false}
          />

          <RegisterActions
            hasPrevious={hasPrevious}
            hasDone={hasDone}
            goPrevious={goPrevious}
            isEditMode={isEditMode}
            isAdmin={isAdmin}
            showSaveButton={!isEditMode}
            showRequestChangeButton={false}
            goNext={() => {
              Modal.confirm({
                content: t('complete.content'),
                okText: t('complete.okText'),
                cancelText: t('complete.cancelText'),
                onOk: this.onMasterSubmit,
              });
            }}
            t={t}
          />
        </Spin>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const documentIds = selectors.getEntityDocumentsList(state);

  const isLoading = getLoadingSelector(
    selectors.getEntityDocumentsList,
    selectors.getEntityDocumentsAddState
  )(state);

  const isStatusChangeLoading = getLoadingSelector(
    selectors.getEntityDocumentsStatusChangeState
  )(state);

  const documents = getLegalEntityDocuments(state, documentIds);

  return {
    isLoading,
    isStatusChangeLoading,
    documents,
  };
};

const mapDispatchToProps = {
  ...actions,
  updateLegalEntity: updateLegalEntityActionCreator,
};

export default connect(mapStateToProps, mapDispatchToProps)(RegisterDocuments);
